import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Building, 
  Save, 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  User, 
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Users,
  Settings,
  Plus,
  Edit,
  Trash2,
  Palette
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CustomTabs } from '@/components/ui/custom-tabs'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ColorPicker } from '@/components/ui/color-picker'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { usePermissions } from '@/hooks/use-permissions'
import { useChurchColors } from '@/hooks/useChurchColors'
import { supabase } from '@/lib/supabase'
import { CargoFormDialog } from '@/components/cargo-form-dialog'
import { ImageUpload } from '@/components/ui/image-upload'
import { formatPhone } from '@/lib/utils'

interface IgrejaData {
  id: string
  nome: string
  endereco: string | null
  cidade: string | null
  estado: string | null
  cep: string | null
  telefone: string | null
  email: string | null
  pastor: string | null
  descricao: string | null
  dominio: string | null
  ativa: boolean | null
  logo_light: string | null
  logo_dark: string | null
  cor_primaria: string | null
  cor_secundaria: string | null
  cor_titulo: string | null
  cor_descricao: string | null
  cor_titulo_light: string | null
  cor_titulo_dark: string | null
  cor_descricao_light: string | null
  cor_descricao_dark: string | null
}

interface CargoIgreja {
  id: string
  igreja_id: string
  nome: string
  descricao: string | null
  nivel_hierarquia: number
  responsabilidades: string[] | null
  ativo: boolean | null
  criado_em: string | null
  atualizado_em: string | null
  criado_por: string | null
}

export function MinhaIgrejaPage() {
  const navigate = useNavigate()
  const { perfil } = useAuth()
  const { canManageChurch } = usePermissions()
  const { revalidate } = useChurchColors()
  const [activeTab, setActiveTab] = useState('dados-basicos')
  const [igreja, setIgreja] = useState<IgrejaData | null>(null)
  const [cargos, setCargos] = useState<CargoIgreja[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Definir as tabs para o CustomTabs
  const igrejaTabsData = [
    { 
      id: 'dados-basicos', 
      label: 'Dados Básicos',
      icon: Settings
    },
    { 
      id: 'estrutura-organizacional', 
      label: 'Estrutura Organizacional',
      icon: Users
    }
  ]

  // Verificar permissões
  useEffect(() => {
    if (!canManageChurch()) {
      navigate('/', { replace: true })
      return
    }
  }, [canManageChurch, navigate])

  // Carregar dados da igreja
  useEffect(() => {
    const loadIgrejaData = async () => {
      if (!perfil?.igreja_id || !supabase) {
        setError('Usuário não está associado a uma igreja')
        setIsLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('igrejas')
          .select('*')
          .eq('id', perfil.igreja_id)
          .single()

        if (error) {
          throw new Error(`Erro ao carregar dados da igreja: ${error.message}`)
        }

        setIgreja(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido')
      } finally {
        setIsLoading(false)
      }
    }

    loadIgrejaData()
  }, [perfil?.igreja_id])

  // Carregar cargos da igreja
  useEffect(() => {
    const loadCargos = async () => {
      if (!perfil?.igreja_id || !supabase) return

      try {
        const { data, error } = await supabase
          .from('cargos_igreja')
          .select('*')
          .eq('igreja_id', perfil.igreja_id)
          .eq('ativo', true)
          .order('nivel_hierarquia', { ascending: true })

        if (error) {
          console.error('Erro ao carregar cargos:', error)
          return
        }

        setCargos(data || [])
      } catch (err) {
        console.error('Erro ao carregar cargos:', err)
      }
    }

    if (activeTab === 'estrutura-organizacional') {
      loadCargos()
    }
  }, [perfil?.igreja_id, activeTab])

  const handleInputChange = (field: keyof IgrejaData, value: string) => {
    if (!igreja) return
    
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone') {
      processedValue = formatPhone(value)
    }
    
    setIgreja({
      ...igreja,
      [field]: processedValue || null
    })
  }

  const handleSave = async () => {
    if (!igreja || !supabase) return

    setIsSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const { error } = await supabase
        .from('igrejas')
        .update({
          nome: igreja.nome,
          endereco: igreja.endereco,
          cidade: igreja.cidade,
          estado: igreja.estado,
          cep: igreja.cep,
          telefone: igreja.telefone,
          email: igreja.email,
          pastor: igreja.pastor,
          descricao: igreja.descricao,
          logo_light: igreja.logo_light,
          logo_dark: igreja.logo_dark,
          cor_primaria: igreja.cor_primaria,
          cor_secundaria: igreja.cor_secundaria,
          cor_titulo: igreja.cor_titulo,
          cor_descricao: igreja.cor_descricao,
          cor_titulo_light: igreja.cor_titulo_light,
          cor_titulo_dark: igreja.cor_titulo_dark,
          cor_descricao_light: igreja.cor_descricao_light,
          cor_descricao_dark: igreja.cor_descricao_dark,
          atualizado_em: new Date().toISOString()
        })
        .eq('id', igreja.id)

      if (error) {
        throw new Error(`Erro ao salvar dados: ${error.message}`)
      }

      setSuccess('Igreja atualizada com sucesso!')
      
      // Revalidar os dados da igreja para refletir as mudanças imediatamente
      await revalidate()
      
      // Recarregar a página após um breve delay para mostrar a mensagem de sucesso
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Carregando dados da igreja...</span>
        </div>
      </div>
    )
  }

  if (error && !igreja) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Minha Igreja</h1>
            <p className="text-muted-foreground">Gerencie os dados da sua igreja</p>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

// Função para criar/atualizar cargo
const handleSaveCargo = async (cargoData: Omit<CargoIgreja, 'id' | 'criado_em' | 'atualizado_em' | 'criado_por'>) => {
  if (!supabase || !perfil?.id) {
    throw new Error('Erro de autenticação')
  }

  const { data, error } = await supabase
    .from('cargos_igreja')
    .insert({
      ...cargoData,
      criado_por: perfil.id,
      criado_em: new Date().toISOString(),
      atualizado_em: new Date().toISOString()
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Erro ao criar cargo: ${error.message}`)
  }

  // Atualizar lista local
  setCargos(prev => [...prev, data].sort((a, b) => a.nivel_hierarquia - b.nivel_hierarquia))
  
  // Mostrar sucesso
  setSuccess('Cargo criado com sucesso!')
  setTimeout(() => setSuccess(null), 3000)
}

// Função para atualizar cargo existente
const handleUpdateCargo = async (cargoId: string, cargoData: Omit<CargoIgreja, 'id' | 'criado_em' | 'atualizado_em' | 'criado_por'>) => {
  if (!supabase) {
    throw new Error('Erro de autenticação')
  }

  const { data, error } = await supabase
    .from('cargos_igreja')
    .update({
      ...cargoData,
      atualizado_em: new Date().toISOString()
    })
    .eq('id', cargoId)
    .select()
    .single()

  if (error) {
    throw new Error(`Erro ao atualizar cargo: ${error.message}`)
  }

  // Atualizar lista local
  setCargos(prev => 
    prev.map(cargo => cargo.id === cargoId ? data : cargo)
      .sort((a, b) => a.nivel_hierarquia - b.nivel_hierarquia)
  )
  
  // Mostrar sucesso
  setSuccess('Cargo atualizado com sucesso!')
  setTimeout(() => setSuccess(null), 3000)
}

// Função para excluir cargo
const handleDeleteCargo = async (cargo: CargoIgreja) => {
  if (!supabase) return

  setIsDeleting(true)
  setError(null)

  try {
    const { error } = await supabase
      .from('cargos_igreja')
      .update({ 
        ativo: false,
        atualizado_em: new Date().toISOString()
      })
      .eq('id', cargo.id)

    if (error) {
      throw new Error(`Erro ao excluir cargo: ${error.message}`)
    }

    // Remover da lista local
    setCargos(prev => prev.filter(c => c.id !== cargo.id))
    
    // Mostrar sucesso
    setSuccess('Cargo excluído com sucesso!')
    setTimeout(() => setSuccess(null), 3000)
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Erro desconhecido')
  } finally {
    setIsDeleting(false)
    // Resetar estado de erro
    setError(null)
  }
}

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building className="h-6 w-6" />
              Minha Igreja
            </h1>
            <p className="text-muted-foreground">Gerencie os dados da sua igreja</p>
          </div>
        </div>

        {activeTab === 'dados-basicos' && (
          <Button
            onClick={handleSave}
            disabled={isSaving || !igreja}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        )}
      </motion.div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>Dados salvos com sucesso!</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <CustomTabs
        tabs={igrejaTabsData}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        className="w-full"
      />

      {/* Conteúdo das Tabs */}
      {activeTab === 'dados-basicos' && (
        <div className="space-y-6">
          {igreja && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Informações Básicas */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Informações Básicas
                    </CardTitle>
                    <CardDescription>
                      Dados principais da igreja
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="nome">Nome da Igreja *</Label>
                      <Input
                        id="nome"
                        value={igreja.nome || ''}
                        onChange={(e) => handleInputChange('nome', e.target.value)}
                        placeholder="Nome da igreja"
                      />
                    </div>

                    <div>
                      <Label htmlFor="pastor">Pastor</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="pastor"
                          className="pl-10"
                          value={igreja.pastor || ''}
                          onChange={(e) => handleInputChange('pastor', e.target.value)}
                          placeholder="Nome do pastor"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="descricao">Descrição</Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Textarea
                          id="descricao"
                          className="pl-10 min-h-[100px]"
                          value={igreja.descricao || ''}
                          onChange={(e) => handleInputChange('descricao', e.target.value)}
                          placeholder="Descrição da igreja, missão, visão..."
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <div>
                        <Label className="text-base font-semibold">Cores do Título</Label>
                        <p className="text-sm text-muted-foreground mb-3">
                          Configure as cores do título para cada tema
                        </p>
                        <div className="grid gap-4 md:grid-cols-2">
                          <ColorPicker
                            label="Tema Claro"
                            value={igreja.cor_titulo_light || '#f97316'}
                            onChange={(color) => handleInputChange('cor_titulo_light', color)}
                            placeholder="#f97316"
                          />
                          <ColorPicker
                            label="Tema Escuro"
                            value={igreja.cor_titulo_dark || '#fb923c'}
                            onChange={(color) => handleInputChange('cor_titulo_dark', color)}
                            placeholder="#fb923c"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-base font-semibold">Cores da Descrição</Label>
                        <p className="text-sm text-muted-foreground mb-3">
                          Configure as cores da descrição para cada tema
                        </p>
                        <div className="grid gap-4 md:grid-cols-2">
                          <ColorPicker
                            label="Tema Claro"
                            value={igreja.cor_descricao_light || '#64748b'}
                            onChange={(color) => handleInputChange('cor_descricao_light', color)}
                            placeholder="#64748b"
                          />
                          <ColorPicker
                            label="Tema Escuro"
                            value={igreja.cor_descricao_dark || '#94a3b8'}
                            onChange={(color) => handleInputChange('cor_descricao_dark', color)}
                            placeholder="#94a3b8"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Contato */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      Contato
                    </CardTitle>
                    <CardDescription>
                      Informações de contato
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="telefone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="telefone"
                          className="pl-10"
                          value={igreja.telefone || ''}
                          onChange={(e) => handleInputChange('telefone', e.target.value)}
                          placeholder="(11) 99999-9999"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          className="pl-10"
                          value={igreja.email || ''}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="contato@igreja.com"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="dominio">Domínio</Label>
                      <Input
                        id="dominio"
                        value={igreja.dominio || ''}
                        onChange={(e) => handleInputChange('dominio', e.target.value)}
                        placeholder="https://minha-igreja.com"
                        disabled
                        className="bg-muted"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        O domínio não pode ser alterado. Entre em contato com o suporte se necessário.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Endereço */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="md:col-span-2"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Endereço
                    </CardTitle>
                    <CardDescription>
                      Localização da igreja
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="md:col-span-2">
                        <Label htmlFor="endereco">Endereço</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="endereco"
                            className="pl-10"
                            value={igreja.endereco || ''}
                            onChange={(e) => handleInputChange('endereco', e.target.value)}
                            placeholder="Rua, número, bairro"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="cep">CEP</Label>
                        <Input
                          id="cep"
                          value={igreja.cep || ''}
                          onChange={(e) => handleInputChange('cep', e.target.value)}
                          placeholder="00000-000"
                        />
                      </div>

                      <div>
                        <Label htmlFor="cidade">Cidade</Label>
                        <Input
                          id="cidade"
                          value={igreja.cidade || ''}
                          onChange={(e) => handleInputChange('cidade', e.target.value)}
                          placeholder="Nome da cidade"
                        />
                      </div>

                      <div>
                        <Label htmlFor="estado">Estado</Label>
                        <Input
                          id="estado"
                          value={igreja.estado || ''}
                          onChange={(e) => handleInputChange('estado', e.target.value)}
                          placeholder="SP"
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Logos */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="md:col-span-2"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Logos da Igreja
                    </CardTitle>
                    <CardDescription>
                      Faça upload das logos para os temas claro e escuro
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-6 md:grid-cols-2">
                      <ImageUpload
                        label="Logo Tema Claro"
                        description="Logo que será exibida no tema claro"
                        currentImageUrl={igreja.logo_light}
                        onImageChange={(url) => handleInputChange('logo_light', url || '')}
                        bucketName="church-logos"
                        folderPath={igreja.id}
                        maxSizeInMB={5}
                      />
                      
                      <ImageUpload
                        label="Logo Tema Escuro"
                        description="Logo que será exibida no tema escuro"
                        currentImageUrl={igreja.logo_dark}
                        onImageChange={(url) => handleInputChange('logo_dark', url || '')}
                        bucketName="church-logos"
                        folderPath={igreja.id}
                        maxSizeInMB={5}
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Cores da Igreja */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="md:col-span-2"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="h-5 w-5" />
                      Cores da Igreja
                    </CardTitle>
                    <CardDescription>
                      Defina as cores que representam sua igreja. Essas cores serão usadas em elementos visuais do sistema.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-6 md:grid-cols-2">
                      <ColorPicker
                        label="Cor Primária"
                        value={igreja.cor_primaria || '#3b82f6'}
                        onChange={(color) => handleInputChange('cor_primaria', color)}
                        placeholder="#3b82f6"
                      />
                      
                      <ColorPicker
                        label="Cor Secundária"
                        value={igreja.cor_secundaria || '#64748b'}
                        onChange={(color) => handleInputChange('cor_secundaria', color)}
                        placeholder="#64748b"
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'estrutura-organizacional' && (
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Estrutura Organizacional
                </CardTitle>
                <CardDescription>
                  Gerencie os cargos e hierarquia da sua igreja
                </CardDescription>
              </CardHeader>
              <CardContent>
                {cargos.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">Nenhum cargo cadastrado</h3>
                    <p className="text-muted-foreground mb-4">
                      Comece criando os cargos da sua igreja para organizar a estrutura hierárquica.
                    </p>
                    <CargoFormDialog
                      trigger={
                        <Button className="gap-2">
                          <Plus className="h-4 w-4" />
                          Criar Primeiro Cargo
                        </Button>
                      }
                      igrejaId={perfil?.igreja_id || ''}
                      onSave={handleSaveCargo}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">
                        {cargos.length} cargo{cargos.length !== 1 ? 's' : ''} cadastrado{cargos.length !== 1 ? 's' : ''}
                      </p>
                      <CargoFormDialog
                        trigger={
                          <Button size="sm" className="gap-2">
                            <Plus className="h-4 w-4" />
                            Novo Cargo
                          </Button>
                        }
                        igrejaId={perfil?.igreja_id || ''}
                        onSave={handleSaveCargo}
                      />
                    </div>

                    <div className="grid gap-4">
                      {cargos.map((cargo) => (
                        <Card key={cargo.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{cargo.nome}</h4>
                                <Badge variant="outline" className="text-xs">
                                  Nível {cargo.nivel_hierarquia}
                                </Badge>
                              </div>
                              {cargo.descricao && (
                                <p className="text-sm text-muted-foreground">{cargo.descricao}</p>
                              )}
                              {cargo.responsabilidades && cargo.responsabilidades.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {cargo.responsabilidades.map((resp, index) => (
                                    <Badge
                                      key={index}
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {resp}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <CargoFormDialog
                                trigger={
                                  <Button variant="outline" size="sm" className="gap-2">
                                    <Edit className="h-4 w-4" />
                                    Editar
                                  </Button>
                                }
                                cargo={cargo}
                                igrejaId={perfil?.igreja_id || ''}
                                onSave={(cargoData) => handleUpdateCargo(cargo.id, cargoData)}
                              />
                              
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="gap-2 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Excluir
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir o cargo "{cargo.nome}"? 
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteCargo(cargo)}
                                      disabled={isDeleting}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      {isDeleting ? (
                                        <>
                                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                          Excluindo...
                                        </>
                                      ) : (
                                        'Excluir'
                                      )}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  )
}